/**
 * UiStyleService — Centralized typography configuration
 *
 * Fetches per-company typography overrides from the API
 * and applies them as CSS custom properties on :root.
 * Falls back to system defaults when values are null.
 */
import { ref, readonly } from "vue";
import { adminApi } from "@/api/adminApi";

// ── Default Typography Values ─────────────────────────────────────────────────
// Must match current existing styles so the system is unchanged without config.
export const TYPOGRAPHY_DEFAULTS = {
  headingFont: "Inter",
  headingFontSize: "24px",
  gridHeaderFont: "Inter",
  gridHeaderFontSize: "14px",
  gridRowFont: "Inter",
  gridRowFontSize: "13px",
  menuFont: "Inter",
  menuFontSize: "14px",
  navigationFont: "Inter",
  navigationFontSize: "15px",
  labelFont: "Inter",
  labelFontSize: "13px",
} as const;

export type TypographyKey = keyof typeof TYPOGRAPHY_DEFAULTS;

export interface TypographyConfig {
  headingFont: string | null;
  headingFontSize: string | null;
  gridHeaderFont: string | null;
  gridHeaderFontSize: string | null;
  gridRowFont: string | null;
  gridRowFontSize: string | null;
  menuFont: string | null;
  menuFontSize: string | null;
  navigationFont: string | null;
  navigationFontSize: string | null;
  labelFont: string | null;
  labelFontSize: string | null;
}

// ── CSS Variable Mapping ──────────────────────────────────────────────────────
const CSS_VAR_MAP: Record<TypographyKey, string> = {
  headingFont: "--heading-font",
  headingFontSize: "--heading-size",
  gridHeaderFont: "--grid-header-font",
  gridHeaderFontSize: "--grid-header-font-size",
  gridRowFont: "--grid-row-font",
  gridRowFontSize: "--grid-row-font-size",
  menuFont: "--menu-font",
  menuFontSize: "--menu-font-size",
  navigationFont: "--nav-font",
  navigationFontSize: "--nav-font-size",
  labelFont: "--label-font",
  labelFontSize: "--label-font-size",
};

// ── Reactive State ────────────────────────────────────────────────────────────
const _config = ref<TypographyConfig>({
  headingFont: null,
  headingFontSize: null,
  gridHeaderFont: null,
  gridHeaderFontSize: null,
  gridRowFont: null,
  gridRowFontSize: null,
  menuFont: null,
  menuFontSize: null,
  navigationFont: null,
  navigationFontSize: null,
  labelFont: null,
  labelFontSize: null,
});

const _loading = ref(false);

/**
 * Resolve a typography value with fallback to defaults.
 */
export function resolveTypography(key: TypographyKey): string {
  return (_config.value[key] as string) ?? TYPOGRAPHY_DEFAULTS[key];
}

/**
 * Apply all typography CSS variables to :root.
 */
function applyCSSVariables(): void {
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
    const value = resolveTypography(key as TypographyKey);
    root.style.setProperty(cssVar, value);
  }
}

/**
 * Fetch typography config from API and apply CSS variables.
 * Silently falls back to defaults if API is unreachable.
 */
export async function loadTypographyConfig(): Promise<void> {
  _loading.value = true;
  try {
    const res = await adminApi.getTypography();
    const config = res.data?.data?.config ?? {};
    _config.value = { ..._config.value, ...config };
  } catch {
    // API not available — use defaults (no-op, config stays null)
  } finally {
    _loading.value = false;
    applyCSSVariables();
  }
}

/**
 * Update typography config via API and re-apply CSS variables.
 */
export async function updateTypographyConfig(
  updates: Partial<TypographyConfig>,
): Promise<void> {
  try {
    const res = await adminApi.updateTypography(
      updates as Record<string, string | null>,
    );
    const config = res.data?.data?.config ?? {};
    _config.value = { ..._config.value, ...config };
  } catch (e) {
    throw e;
  } finally {
    applyCSSVariables();
  }
}

/**
 * Apply a local preview without saving to the API.
 * Used for the admin preview panel.
 */
export function previewTypography(updates: Partial<TypographyConfig>): void {
  _config.value = { ..._config.value, ...updates };
  applyCSSVariables();
}

/**
 * Reset to defaults (locally only — does not call API).
 */
export function resetToDefaults(): void {
  const reset: TypographyConfig = {
    headingFont: null,
    headingFontSize: null,
    gridHeaderFont: null,
    gridHeaderFontSize: null,
    gridRowFont: null,
    gridRowFontSize: null,
    menuFont: null,
    menuFontSize: null,
    navigationFont: null,
    navigationFontSize: null,
    labelFont: null,
    labelFontSize: null,
  };
  _config.value = reset;
  applyCSSVariables();
}

// Expose reactive state
export const typographyConfig = readonly(_config);
export const typographyLoading = readonly(_loading);
